import * as React from "react";
import { connect } from "react-redux";
import { isEmpty as _isEmpty } from "lodash";
import * as moment from "moment";
import { groupBy as _groupBy, chain as _chain } from "lodash"
import { AllStaticDataActions } from "../actions/AllStaticDataActions";
import { ApiManager } from "../api/ApiManager";
import { generateNDaysDateObjects, isSameDayForTimestamps } from "../utils/DateUtils";
import AppLoader from "../components/utils/AppLoader";
import AppUtils from "../utils/AppUtils";
import CollapsibleCardView from "../components/CollapsibleCardView";

const NO_OF_DAYS_PER_VIEW = 4;

interface CovidAssessmentActionProps {
  fetchCovidMetricIds: () => Promise<Array<MetricsIds>>,
  fetchCovidVitals: (startEpoch: number, endEpoch: number, metricIds: string[], patientId: number, curefitUserId: string) => Promise<Array<Vital>> ,
  getIsActiveCovidPack: (userId: string) => Promise<any>
}

interface CovidAssessmentProps {
  patientId: number,
  curefitUserId: number
}

export interface MetricsIds {
  metricIds: string[],
  symptoms: string[],
  vitals: string[]
}

interface RangeBucket {
  rangeMax: number,
  rangeMin: number,
  displayLabel: string  
}

interface Metric {
  name: string,
  rangeBuckets: Array<RangeBucket>
  id: number
}

export interface Vital {
  id: number,
  metricDate: number,
  value: number,
  metric: Metric,
  metricId?: number
}

interface CellValue {
  displayValue: string,
  metricTime?: string
  className?: string
}

interface StateVitalsData {
  [key: string]: Vital[]
}

interface SymptomsVitalsMap {
  symptoms: string[],
  vitals: string[]
}

const COLOR_CLASS_SEVERITY_MAP: {[key: string]: string } = {
  "RED": "covid-value-issue",
  "ORANGE": "covid-value-issue"
}

const CovidAssessmentRecords: React.SFC<CovidAssessmentActionProps & CovidAssessmentProps> = props => {
  let [vitalsData, setVitalsData] = React.useState<StateVitalsData>({});
  let [isLoading, setLoading] = React.useState<Boolean>(true);
  let [lastDate, setLastDate] = React.useState<Date>(new Date());
  let [symptomsVitalsIdsMap, setSymptomsVitalsIdsMap] = React.useState<SymptomsVitalsMap>({
    symptoms: [],
    vitals: []
  })
  let [highlightedDate, setHighlightedDate] = React.useState(lastDate);

  React.useEffect(() => {
    props.fetchCovidMetricIds().then((response: Array<MetricsIds>) => {
      const endEpoch = lastDate.getTime();
      const startEpoch = moment(lastDate).subtract(NO_OF_DAYS_PER_VIEW, "days").toDate().getTime();
      setSymptomsVitalsIdsMap({
        symptoms: response[0].symptoms,
        vitals: response[0].vitals
      })
      props.fetchCovidVitals(startEpoch, endEpoch, response[0].metricIds, props.patientId, props.curefitUserId).then((vitalsResponse: Array<Vital>) => {
        vitalsResponse = vitalsResponse.map((item: Vital) => { item.metricId = item.metric.id; return item; });
        setVitalsData(_chain(vitalsResponse).groupBy('metricId').value());
        setLoading(false);
      });
    })
  }, [lastDate]);

  const goBackOneWeek = () => {
    setLoading(true);
    setLastDate(moment(lastDate).subtract(NO_OF_DAYS_PER_VIEW, "days").toDate());
  }

  const goAheadOneWeek = () => {
    setLoading(true);
    setLastDate(moment(lastDate).add(NO_OF_DAYS_PER_VIEW, "days").toDate());
  }

  const getFormattedDayString = (date: Date): string => {
    return moment(date).format("D, ddd");
  }

  const onSelectColumn = (columnEpoch: number) => {
    setHighlightedDate(new Date(columnEpoch));
  }

  const renderCellData = (cellsData: any[]): JSX.Element[] => {
    let values: CellValue[] = [];
    if (_isEmpty(cellsData)) {
      values.push({
        displayValue: "-"
      });
    } else {
      cellsData.forEach((cellData) => {
        if (!AppUtils.isDefined(cellData && cellData.value)) {
          values.push({
            displayValue: "-"
          });
        }

        if (cellData.metric) {
          if (cellData.metric && cellData.metric.rangeBuckets && cellData.metric.rangeBuckets.length > 0) {
            for (let i = 0; i < cellData.metric.rangeBuckets.length; i++) {
              const rangeBucket = cellData.metric.rangeBuckets[i]
              if (cellData.value >= rangeBucket.rangeMin && cellData.value <= rangeBucket.rangeMax) {
                values.push({
                  metricTime: moment(new Date(cellData.metricDate)).format("h:mm A"),
                  displayValue: symptomsVitalsIdsMap.symptoms.includes(cellData.metricId.toString()) ? rangeBucket.displayLabel : cellData.value,
                  className: rangeBucket.meta && COLOR_CLASS_SEVERITY_MAP[rangeBucket.meta.color]
                });
              }
            }
          } else {
            values.push({
              metricTime: moment(new Date(cellData.metricDate)).format("h:mm A"),
              displayValue: cellData.value
            });
          }
        }
      }); 
    }
    return values.map((value: any) => {
      return (
        <div className="covid-value-container" key={value.metricTime}>
          {value.metricTime && values.length > 0 && (
            <div className="covid-value-time">{value.metricTime}</div>
          )}
          <div key={value} className={`covid-value ${value.className}`}>{value.displayValue}</div>
        </div>
      );
    })
  }

  const dateEpochs: number[] = generateNDaysDateObjects(lastDate, NO_OF_DAYS_PER_VIEW).map((date: Date) => { return date.getTime(); }); 

  return (
    <CollapsibleCardView titleComponent="Covid Self Assessment" showCollapseButton={true}>
      <section className="covid-assessment-table collapsible-card">
        {!isLoading && (
          <>
            <div className="covid-content">
              <div className="covid-row-body covid-row-body-header">
                {dateEpochs.map((dateEpoch, index) => {
                  if (index === 0) {
                    return (
                      <>
                        <div className="covid-row-cell" onClick={goBackOneWeek}>
                          <img className="prev-week-icon" src="/images/icons/back-grey.png" />
                        </div>
                        <div
                          className={`covid-row-cell covid-row-cell-header ${isSameDayForTimestamps(dateEpoch, highlightedDate.getTime()) && "covid-selected-cell"}`} onClick={() => { onSelectColumn(dateEpoch) }}
                        >
                          {getFormattedDayString(new Date(dateEpoch))}
                        </div>
                      </>
                    )
                  }
                  return (
                    <div
                      onClick={() => { onSelectColumn(dateEpoch) }}
                      className={`covid-row-cell covid-row-cell-header ${isSameDayForTimestamps(dateEpoch, highlightedDate.getTime()) && "covid-selected-cell"}`}
                    >
                      {getFormattedDayString(new Date(dateEpoch))}
                      {index === dateEpochs.length - 1 && !isSameDayForTimestamps(new Date().getTime(), lastDate.getTime()) && (
                        <img className="next-week-icon" src="/images/icons/forward-grey.svg" onClick={goAheadOneWeek} />
                      )}
                    </div>
                  );
                })}
              </div>
              {!_isEmpty(vitalsData) && Object.keys(vitalsData).map((vitalsKey: string) => {
                return (
                  <div className="covid-row-body">
                    <div className="covid-row-cell">
                      <span className="covid-row-vital">{vitalsData[vitalsKey][0].metric.name}</span>
                    </div>
                    {dateEpochs.map((dayEpoch: number): JSX.Element => {
                      const dayVitalData: any[] = vitalsData[vitalsKey].filter((vitalDayData) => { return isSameDayForTimestamps(vitalDayData.metricDate, dayEpoch) })
                      return (
                        <div className={`covid-row-cell ${isSameDayForTimestamps(dayEpoch, highlightedDate.getTime()) && "covid-selected-cell"}`}>
                          {renderCellData(dayVitalData)}
                        </div>
                      );
                    })}
                  </div>
                )
              })}
              {_isEmpty(vitalsData) && (
                <div className="covid-no-data">No Data for {getFormattedDayString(new Date(dateEpochs[0]))} - {getFormattedDayString(new Date(dateEpochs[dateEpochs.length - 1]))}</div>
              )}
            </div>
          </>
        )}
        {isLoading && (
          <AppLoader />
        )}
      </section>
    </CollapsibleCardView>
  )
}

const mapDispatchToProps = () => {
  return {
    fetchCovidMetricIds: () => {
      return AllStaticDataActions.fetchCovidMetricIds();
    },
    fetchCovidVitals: (startEpoch: number, endEpoch: number, metricIds: string[], patientId: number, userId: string) => {
      return ApiManager.getAlbusApis().getCovidVitals(startEpoch, endEpoch, metricIds, patientId, userId);
    },
    getIsActiveCovidPack: (userId: number) => {
      return ApiManager.getAlbusApis().getIsActiveCovidPack(userId);
    }
  }
}

export default connect<any, CovidAssessmentActionProps, CovidAssessmentProps>(null, mapDispatchToProps)(CovidAssessmentRecords);
